import { trpc } from '@/api/trpc';
import { Modal } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { useAppQuery } from '@/hooks/useAppQuery';
import { formatMetric, formatParamVector, formatPercent, type EntryOptimizerCandidate } from '../entryOptimizerUtils';

/** Per-fold drill-down: how the candidate's lift held up across time blocks and the holdout. */
const CandidateFoldsModal = ({
    candidate,
    jobId,
    onClose
}: {
    candidate: EntryOptimizerCandidate | null;
    jobId: number;
    onClose: () => void;
}) => {
    const foldsQuery = useAppQuery({
        ...trpc.entryOptimizer.candidateFolds.queryOptions({ candidateId: candidate?.id ?? 0, jobId }),
        enabled: candidate !== null
    });

    const folds = foldsQuery.data?.folds ?? [];

    return (
        <Modal
            isOpen={candidate !== null}
            size="lg"
            title={candidate ? `Folds — ${formatParamVector(candidate)}` : 'Folds'}
            onClose={onClose}
        >
            {foldsQuery.isLoading ? (
                <DataLoadingSpinner text="Loading folds..." />
            ) : folds.length === 0 ? (
                <div className="text-sm text-zinc-500">No fold records for this candidate (gate-blocked before CV).</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-zinc-800 text-left text-xs uppercase tracking-wide text-zinc-500">
                                <th className="px-2 py-2">Fold</th>
                                <th className="px-2 py-2">Window</th>
                                <th className="px-2 py-2 text-right">Entries</th>
                                <th className="px-2 py-2 text-right">Baseline</th>
                                <th className="px-2 py-2 text-right">Candidate</th>
                                <th className="px-2 py-2 text-right">Lift</th>
                                <th className="px-2 py-2 text-right">Win Rate</th>
                                <th className="px-2 py-2 text-right">Entities</th>
                            </tr>
                        </thead>
                        <tbody>
                            {folds.map((fold) => (
                                <tr className="border-b border-zinc-900" key={fold.id}>
                                    <td className="px-2 py-2 text-zinc-300">
                                        {fold.foldKind === 'holdout' ? 'Holdout' : `CV ${fold.foldIndex + 1}`}
                                    </td>
                                    <td className="px-2 py-2 text-xs text-zinc-500">
                                        {new Date(fold.startTimestamp * 1000).toLocaleDateString()} –{' '}
                                        {new Date(fold.endTimestamp * 1000).toLocaleDateString()}
                                    </td>
                                    <td className="px-2 py-2 text-right text-zinc-300">{fold.entryCount.toLocaleString()}</td>
                                    <td className="px-2 py-2 text-right text-zinc-300">{formatMetric(fold.baselineMeanProfit)}</td>
                                    <td className="px-2 py-2 text-right text-zinc-300">{formatMetric(fold.candidateMeanProfit)}</td>
                                    <td
                                        className={`px-2 py-2 text-right ${Number(fold.lift) > 0 ? 'text-green-300' : Number(fold.lift) < 0 ? 'text-red-300' : 'text-zinc-300'}`}
                                    >
                                        {formatMetric(fold.lift)}
                                    </td>
                                    <td className="px-2 py-2 text-right text-zinc-300">{formatPercent(fold.winRate)}</td>
                                    <td className="px-2 py-2 text-right text-zinc-300">{fold.uniqueEntities ?? '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Modal>
    );
};

export default CandidateFoldsModal;
